/**
 * DynamicDates - Custom element for calculating and displaying dynamic dates
 * Used in shipping checkpoints and estimated shipping blocks
 */

class DynamicDates extends HTMLElement {
  constructor() {
    super();
    
    // Get current date
    this.currentDate = new Date();
    
    // Read date format from data attribute (handle kebab-case)
    this.dateFormat = this.dataset.dateFormat || this.getAttribute('data-date-format') || 'mm_dd';
    
    // Parse day and month labels (handle kebab-case and different attribute names)
    const dayLabelsStr = this.dataset.dayLabels || this.getAttribute('data-day-labels') || this.dataset.daysLabels || '';
    const monthLabelsStr = this.dataset.monthLabels || this.getAttribute('data-month-labels') || this.dataset.monthsLabels || '';
    
    // Default labels
    const defaultDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const defaultMonths = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    this.days = dayLabelsStr ? dayLabelsStr.split(',').map(d => d.trim()) : defaultDays;
    this.months = monthLabelsStr ? monthLabelsStr.split(',').map(m => m.trim()) : defaultMonths;
    
    // Find all elements that need date replacement
    this.elementsToChange = this.querySelectorAll('[data-dynamic-date="true"]');
    
    // Initialize dates
    this.updateDates();
  }
  
  /**
   * Add days to a date
   */
  addDays(date, days) {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  }
  
  /**
   * Get ordinal suffix for day (st, nd, rd, th)
   */
  getOrdinalSuffix(day) {
    if (day === 1 || day === 21 || day === 31) return 'st';
    if (day === 2 || day === 22) return 'nd';
    if (day === 3 || day === 23) return 'rd';
    return 'th';
  }
  
  /**
   * Format date based on date format setting
   */
  formatDate(date, format) {
    const day = date.getDate();
    const month = date.getMonth();
    const year = date.getFullYear();
    const dayOfWeek = date.getDay();
    const suffix = this.getOrdinalSuffix(day);
    
    const dayName = this.days[dayOfWeek] || this.days[dayOfWeek];
    const monthName = this.months[month] || this.months[month];
    
    switch (format) {
      case 'day_mm_dd':
        return `${dayName}, ${monthName} ${day}${suffix}`;
      
      case 'day_dd_mm':
        return `${dayName}, ${day}. ${monthName}`;
      
      case 'mm_dd':
        return `${monthName} ${day}${suffix}`;
      
      case 'dd_mm':
        return `${day}. ${monthName}`;
      
      case 'dd_mm_no_dot':
        return `${day} ${monthName}`;
      
      case 'day_dd_mm_numeric':
        const dayStr1 = String(day).length > 1 ? day : '0' + day;
        const monthStr1 = String(month + 1).length > 1 ? (month + 1) : '0' + (month + 1);
        return `${dayName}, ${dayStr1}. ${monthStr1}.`;
      
      case 'dd_mm_numeric':
        const dayStr2 = String(day).length > 1 ? day : '0' + day;
        const monthStr2 = String(month + 1).length > 1 ? (month + 1) : '0' + (month + 1);
        return `${dayStr2}. ${monthStr2}.`;
      
      default:
        return `${monthName} ${day}${suffix}`;
    }
  }
  
  /**
   * Update all date placeholders
   */
  updateDates() {
    this.elementsToChange.forEach(element => {
      const text = element.dataset.text || '';
      // Handle both camelCase and kebab-case attributes
      const minDays = parseInt(element.dataset.minDays || element.getAttribute('data-min-days')) || 0;
      const maxDays = parseInt(element.dataset.maxDays || element.getAttribute('data-max-days')) || 0;
      
      // Calculate start and end dates
      const startDate = this.addDays(this.currentDate, minDays);
      const endDate = this.addDays(this.currentDate, maxDays);
      
      // Format dates
      const formattedStartDate = this.formatDate(startDate, this.dateFormat);
      const formattedEndDate = this.formatDate(endDate, this.dateFormat);
      
      // Replace placeholders
      let updatedText = text.replace(/\[start_date\]/g, formattedStartDate);
      updatedText = updatedText.replace(/\[end_date\]/g, formattedEndDate);
      
      // Update element HTML to preserve formatting like <strong> tags
      // Text comes from Shopify schema (trusted source), so innerHTML is safe
      element.innerHTML = updatedText;
    });
  }
}

// Register the custom element
if (!customElements.get('dynamic-dates')) {
  customElements.define('dynamic-dates', DynamicDates);
}

